class MonthlyHighlightsList extends HTMLElement {
  apiPage = 1;
  viewPage = 0;
  allVideos = [];
  hasMore = true;
  pageSize = 8;
  viewPageSize = 4;
  isLoading = false;
  videoObserver = null;

  connectedCallback() {
    this.shopSelect = this.dataset.shopSelect;
    this.baseUrl =
      this.dataset.requestType === 'prod'
        ? 'https://h130-app-server-us.hoverx1.cn'
        : 'https://h130-app-server-test-us.hoverx1.cn';
    if (this.shopSelect === 'JP') {
      this.baseUrl =
        this.dataset.requestType === 'prod'
          ? 'https://h130-app-server-jp.hoverx1.cn'
          : 'https://h130-app-server-test-jp.hoverx1.cn';
    }
    document.addEventListener('DOMContentLoaded', () => {
      this.initGrid();
      this.initArrows();
      this.initVideoClick();
      this.fetchVideos();
      this.initResize();
    });
  }

  initResize() {
    let wasDesktop = this.isDesktop();
    window.addEventListener('resize', () => {
      const isDesktop = this.isDesktop();
      if (isDesktop !== wasDesktop) {
        wasDesktop = isDesktop;
        if (isDesktop) {
          this.showActivePage();
          this.updateArrowVisibility();
        } else {
          this.grid
            .querySelectorAll('.mh-page-group')
            .forEach((g) => g.classList.remove('mh-active'));
          // 若首次渲染时是 PC，footer 尚未创建，切到移动端需补建
          this.ensureMobileFooter();
          this.updateMobileArrowVisibility();
        }
      }
    });
  }

  initGrid() {
    this.grid = this.querySelector('#mh-video-grid');
    this.arrowLeft = this.querySelector('.mh-arrow-left');
    this.arrowRight = this.querySelector('.mh-arrow-right');
  }

  initArrows() {
    this.arrowRight.addEventListener('click', () => {
      this.isDesktop() ? this.navigateView(1) : this.scrollView(1);
    });
    this.arrowLeft.addEventListener('click', () => {
      this.isDesktop() ? this.navigateView(-1) : this.scrollView(-1);
    });
    this.grid.addEventListener(
      'scroll',
      () => {
        this.updateMobileArrowVisibility();
        this.checkScrollEnd();
      },
      { passive: true }
    );
  }

  // 手指滑动接近右侧末尾时加载下一页（IntersectionObserver 在横向滚动容器里不够可靠，此处做兜底）
  checkScrollEnd() {
    if (this.isDesktop() || this.isLoading || !this.hasMore) return;
    const remaining =
      this.grid.scrollWidth - this.grid.clientWidth - this.grid.scrollLeft;
    // 距末尾不足一屏宽时预加载，避免滑到尽头才等待
    if (remaining <= this.grid.clientWidth * 0.5) {
      this.fetchVideos();
    }
  }

  initVideoClick() {
    this.grid.addEventListener('click', (e) => {
      const videoItem = e.target.closest('.mh-video-item');
      if (!videoItem) return;
      e.stopPropagation();
      const idx = parseInt(videoItem.dataset.videoIndex, 10);
      if (isNaN(idx)) return;
      const dialog = this.querySelector('monthly-highlights-dialog');
      if (dialog) {
        dialog.openWithVideos(this.allVideos, idx);
      }
    });
  }

  isDesktop() {
    return window.innerWidth >= 1024;
  }

  viewPageCount() {
    return Math.ceil(this.allVideos.length / this.viewPageSize);
  }

  // PC: switch active page group (show/hide)
  navigateView(direction) {
    const totalViewPages = this.viewPageCount();
    const nextPage = this.viewPage + direction;

    if (nextPage < 0) return;

    if (nextPage >= totalViewPages) {
      if (this.hasMore) {
        this.fetchVideos().then(() => {
          this.viewPage = nextPage;
          this.showActivePage();
          this.updateArrowVisibility();
        });
      }
      return;
    }

    this.viewPage = nextPage;
    this.showActivePage();
    this.updateArrowVisibility();

    if (nextPage >= totalViewPages - 1 && this.hasMore) {
      this.fetchVideos();
    }
  }

  // Mobile: horizontal scroll by one page group
  scrollView(direction) {
    const groups = this.grid.querySelectorAll('.mh-page-group');
    if (!groups.length) return;
    const current = this.currentMobilePage(groups);
    const nextPage = current + direction;

    // 分组比视口宽，停在首/末组内部时按钮仍是可用态；此时没有下一组可翻，
    // 就滚到该方向的尽头，把组内剩余部分露出来，否则点击无任何反馈
    if (nextPage < 0) {
      this.scrollToEdge(0);
      return;
    }

    if (nextPage >= groups.length) {
      if (this.hasMore) {
        this.fetchVideos().then(() => {
          const updated = this.grid.querySelectorAll('.mh-page-group');
          const target = updated[nextPage];
          if (target) this.scrollToGroup(target);
          setTimeout(() => this.updateMobileArrowVisibility(), 400);
        });
      } else {
        this.scrollToEdge(this.grid.scrollWidth - this.grid.clientWidth);
      }
      return;
    }

    this.scrollToGroup(groups[nextPage]);
    setTimeout(() => this.updateMobileArrowVisibility(), 400);
  }

  scrollToEdge(left) {
    this.grid.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    setTimeout(() => this.updateMobileArrowVisibility(), 400);
  }

  // 滚动到某个分组；减掉容器左内边距，使其与 scroll-padding-left 的吸附位一致
  scrollToGroup(group) {
    const paddingLeft =
      parseFloat(getComputedStyle(this.grid).paddingLeft) || 0;
    const left = group.offsetLeft - this.grid.offsetLeft - paddingLeft;
    this.grid.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }

  currentMobilePage(groups) {
    const paddingLeft =
      parseFloat(getComputedStyle(this.grid).paddingLeft) || 0;
    const scrollLeft = this.grid.scrollLeft;
    let closest = 0;
    let minDist = Infinity;
    groups.forEach((g, i) => {
      const dist = Math.abs(
        g.offsetLeft - this.grid.offsetLeft - paddingLeft - scrollLeft
      );
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });
    return closest;
  }

  showActivePage() {
    if (!this.isDesktop()) return;
    const groups = this.grid.querySelectorAll('.mh-page-group');
    groups.forEach((g, i) => {
      g.classList.toggle('mh-active', i === this.viewPage);
    });
    this.initVideoAutoplay();
  }

  updateArrowVisibility() {
    if (!this.isDesktop()) return;
    this.viewPage > 0
      ? this.arrowLeft.classList.remove('mh-disabled')
      : this.arrowLeft.classList.add('mh-disabled');

    if (this.viewPage >= this.viewPageCount() - 1 && !this.hasMore) {
      this.arrowRight.classList.add('mh-disabled');
    } else {
      this.arrowRight.classList.remove('mh-disabled');
    }
  }

  updateMobileArrowVisibility() {
    if (this.isDesktop()) return;
    // 无数据时两个按钮都不可用
    if (!this.grid.querySelector('.mh-page-group')) {
      this.arrowLeft.classList.add('mh-disabled');
      this.arrowRight.classList.add('mh-disabled');
      return;
    }
    const atStart = this.grid.scrollLeft <= 4;
    atStart
      ? this.arrowLeft.classList.add('mh-disabled')
      : this.arrowLeft.classList.remove('mh-disabled');

    const scrollEnd = this.grid.scrollWidth - this.grid.clientWidth;
    const atEnd = this.grid.scrollLeft >= scrollEnd - 4;
    if (atEnd && !this.hasMore) {
      this.arrowRight.classList.add('mh-disabled');
    } else {
      this.arrowRight.classList.remove('mh-disabled');
    }
  }

  fetchVideos() {
    if (this.isLoading || (!this.hasMore && this.apiPage > 1)) {
      return Promise.resolve();
    }
    this.isLoading = true;
    return httpRequest
      .get(
        '/shopify/media-list',
        {
          page: this.apiPage,
          pageSize: this.pageSize,
          shop: 'FLIGHT_CLUB',
        },
        { baseUrl: this.baseUrl }
      )
      .then((res) => {
        if (res.code === 200) {
          const newVideos = res.data.list;
          const isFirst = this.apiPage === 1;

          if (isFirst) {
            this.allVideos = newVideos;
          } else {
            this.allVideos = this.allVideos.concat(newVideos);
          }

          if (newVideos.length < this.pageSize) {
            this.hasMore = false;
          }

          this.apiPage++;
          this.renderVideos(newVideos, isFirst);
          this.updateArrowVisibility();
          this.updateMobileArrowVisibility();
        }
      })
      .catch((err) => {
        console.error('Monthly Highlights fetch error:', err);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  renderVideos(newVideos, isFirst) {
    if (isFirst) {
      this.grid.innerHTML = '';
      this.ensureMobileFooter();
    }

    const startIdx = this.allVideos.length - newVideos.length;
    for (let i = 0; i < newVideos.length; i += this.viewPageSize) {
      const chunk = newVideos.slice(i, i + this.viewPageSize);
      const globalStartIdx = startIdx + i;
      const viewPageIdx = Math.floor(globalStartIdx / this.viewPageSize);
      const groupHtml = this.buildPageGroup(chunk, globalStartIdx, viewPageIdx);

      const footer = this.grid.querySelector('.mh-grid-footer');
      if (footer) {
        footer.insertAdjacentHTML('beforebegin', groupHtml);
      } else {
        this.grid.insertAdjacentHTML('beforeend', groupHtml);
      }
    }

    if (isFirst) {
      this.showActivePage();
    }

    if (!this.hasMore) {
      this.hideFooter();
    } else {
      // 新分组插在 footer 之前，重新建立观察以保证后续滑动仍能触发
      this.initMobileFooterObserver();
    }

    this.initLazyLoad();
    this.initVideoAutoplay();
    this.updateMobileArrowVisibility();
  }

  buildPageGroup(videos, globalStartIdx, viewPageIdx) {
    const template = this.getTemplate('mh-video-page-template');
    let html = template.replace(/{pageIndex}/g, viewPageIdx);

    for (let i = 0; i < 4; i++) {
      const video = videos[i];
      const absIdx = globalStartIdx + i;
      const displayKey = `display${i + 1}`;
      const display = video ? '' : 'tw-hidden';

      html = html.replace(new RegExp(`{${displayKey}}`, 'g'), display);
      html = html.replace(new RegExp(`{idx${i}}`, 'g'), absIdx);

      if (video) {
        const itemJson = encodeURIComponent(JSON.stringify(video));
        html = html.replace(new RegExp(`{item${i}}`, 'g'), itemJson);

        // positions 0 and 3 are big videos (auto-play), 1 and 2 are small (poster only)
        if (i === 0 || i === 3) {
          html = html.replace(new RegExp(`{video${i}}`, 'g'), video.videoUrl);
          html = html.replace(
            new RegExp(`{poster${i}}`, 'g'),
            video.pictureUrl
          );
        } else if (i === 1) {
          html = html.replace(/{thumb1}/g, video.pictureUrl);
        } else if (i === 2) {
          html = html.replace(/{thumb2}/g, video.pictureUrl);
        }
      }
    }

    return html;
  }

  getTemplate(id) {
    const tpl = this.querySelector(`#${id}`);
    return tpl ? tpl.innerHTML : '';
  }

  initMobileFooterObserver() {
    const footer = this.grid.querySelector('.mh-grid-footer');
    if (!footer) return;
    if (this._footerObserver) this._footerObserver.disconnect();
    this._footerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isDesktop()) {
            this.fetchVideos();
          }
        });
      },
      // root 设为滚动容器、横向留出提前量，否则横向滚动时不易命中
      { root: this.grid, rootMargin: '0px 200px 0px 0px' }
    );
    this._footerObserver.observe(footer);
  }

  // 确保移动端末尾存在 loading footer（PC→移动端切换时补建）
  ensureMobileFooter() {
    if (this.isDesktop() || !this.hasMore) return;
    if (this.grid.querySelector('.mh-grid-footer')) return;
    const footerHtml = this.getTemplate('mh-grid-footer');
    if (!footerHtml) return;
    this.grid.insertAdjacentHTML('beforeend', footerHtml);
    this.initMobileFooterObserver();
  }

  hideFooter() {
    const footer = this.grid.querySelector('.mh-grid-footer');
    if (footer) footer.style.display = 'none';
  }

  initLazyLoad() {
    if (typeof lozad !== 'undefined') {
      const observer = lozad('.mh-lozad', { rootMargin: '30px 0px' });
      observer.observe();
    }
  }

  initVideoAutoplay() {
    if (this.videoObserver) {
      this.videoObserver.disconnect();
    }
    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            const source = video.querySelector('source');
            if (source && source.dataset.src && !source.src) {
              source.src = source.dataset.src;
              video.load();
            }
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { rootMargin: '50px 0px', threshold: 0.3 }
    );
    this.grid.querySelectorAll('.mh-lazy-video').forEach((video) => {
      this.videoObserver.observe(video);
    });
  }

  loadNextPage() {
    this.fetchVideos();
  }
}

customElements.define('monthly-highlights-list', MonthlyHighlightsList);
